import {
  AlertTriangle,
  Camera,
  CameraOff,
  CheckCircle2,
  Clock3,
  FlaskConical,
  Keyboard,
  ScanLine,
  Square,
  UserCheck,
  Users,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Avatar, Button, Modal, PageHeader, StatusBadge } from "../components/ui";
import { formatDayDate, formatTime, todayISO } from "../lib/utils";
import { useApp } from "../state/AppContext";
import type {
  AttendanceMethod,
  AttendanceStatus,
  ScanResult,
} from "../types";
import "../styles/scanner.css";

type CameraState =
  | "idle"
  | "requesting"
  | "active"
  | "paused"
  | "denied"
  | "unavailable"
  | "disabled";

interface DetectedBarcode {
  rawValue?: string;
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}

type BarcodeDetectorConstructor = new (options?: {
  formats?: string[];
}) => BarcodeDetectorLike;

const cameraStateLabel: Record<CameraState, string> = {
  idle: "Kamera belum dimulakan",
  requesting: "Meminta akses kamera…",
  active: "Kamera aktif",
  paused: "Imbasan dijeda",
  denied: "Akses kamera disekat",
  unavailable: "Kamera tidak tersedia",
  disabled: "Kamera dimatikan",
};

const resultHeading = (result: ScanResult) => {
  if (result.kind === "success") return "Kehadiran direkod";
  if (result.kind === "duplicate") return "Sudah direkod hari ini";
  if (result.kind === "inactive") return "Profil murid tidak aktif";
  if (result.student) return "Murid di luar skop kelas";
  return "Kod tidak ditemui";
};

export function ScannerPage() {
  const {
    session,
    students,
    attendance,
    classes,
    settings,
    markAttendance,
  } = useApp();
  const defaultScope =
    session?.role === "teacher" && session.assignedClassId
      ? session.assignedClassId
      : "all";
  const [selectedClassId, setSelectedClassId] = useState(defaultScope);
  const [cameraState, setCameraState] = useState<CameraState>(
    settings.cameraEnabled ? "idle" : "disabled",
  );
  const [cameraMessage, setCameraMessage] = useState(
    settings.cameraEnabled
      ? "Mulakan kamera apabila anda bersedia."
      : "Pentadbir telah mematikan kamera. Simulasi dan kod manual masih boleh digunakan.",
  );
  const [manualCode, setManualCode] = useState("");
  const [lastPayload, setLastPayload] = useState("");
  const [result, setResult] = useState<ScanResult | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<BarcodeDetectorLike | null>(null);
  const detectionFrameRef = useRef<number | null>(null);
  const detectionBusyRef = useRef(false);
  const detectionEnabledRef = useRef(false);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const detectedPayloadHandlerRef = useRef<(payload: string) => void>(() => undefined);

  const teacherIsRestricted =
    session?.role === "teacher" &&
    !settings.permissions.teacherCanViewOtherClasses;
  const canChooseScope = !teacherIsRestricted;

  const availableClasses = useMemo(() => {
    if (!teacherIsRestricted) return classes;
    return classes.filter((schoolClass) => schoolClass.id === session?.assignedClassId);
  }, [classes, session?.assignedClassId, teacherIsRestricted]);

  useEffect(() => {
    const nextScope =
      session?.role === "teacher" && session.assignedClassId
        ? session.assignedClassId
        : "all";
    setSelectedClassId(nextScope);
  }, [session?.id, session?.assignedClassId, session?.role]);

  useEffect(() => {
    if (
      teacherIsRestricted &&
      session?.assignedClassId &&
      selectedClassId !== session.assignedClassId
    ) {
      setSelectedClassId(session.assignedClassId);
    }
  }, [selectedClassId, session?.assignedClassId, teacherIsRestricted]);

  const authorisedStudents = useMemo(() => {
    if (!teacherIsRestricted || !session?.assignedClassId) return students;
    return students.filter((student) => student.classId === session.assignedClassId);
  }, [session?.assignedClassId, students, teacherIsRestricted]);

  const scopedStudents = useMemo(
    () =>
      selectedClassId === "all"
        ? authorisedStudents
        : authorisedStudents.filter((student) => student.classId === selectedClassId),
    [authorisedStudents, selectedClassId],
  );

  const today = todayISO();
  const todayRecords = useMemo(
    () => attendance.filter((record) => record.date === today),
    [attendance, today],
  );
  const markedStudentIds = useMemo(
    () => new Set(todayRecords.map((record) => record.studentId)),
    [todayRecords],
  );
  const scopedStudentIds = useMemo(
    () => new Set(scopedStudents.map((student) => student.id)),
    [scopedStudents],
  );
  const nextUnmarkedStudent = useMemo(
    () =>
      scopedStudents.find(
        (student) => student.active && !markedStudentIds.has(student.id),
      ),
    [markedStudentIds, scopedStudents],
  );
  const recordedCount = scopedStudents.filter((student) =>
    markedStudentIds.has(student.id),
  ).length;
  const scopeName =
    selectedClassId === "all"
      ? "Semua kelas"
      : classes.find((schoolClass) => schoolClass.id === selectedClassId)?.name ??
        "Kelas";

  const pauseDetection = useCallback(() => {
    detectionEnabledRef.current = false;
    if (detectionFrameRef.current !== null) {
      cancelAnimationFrame(detectionFrameRef.current);
      detectionFrameRef.current = null;
    }
  }, []);

  const beginDetection = useCallback(() => {
    if (!detectorRef.current || !videoRef.current) return;
    pauseDetection();
    detectionEnabledRef.current = true;

    const detectFrame = async () => {
      if (!detectionEnabledRef.current) return;
      const detector = detectorRef.current;
      const video = videoRef.current;

      if (
        detector &&
        video &&
        video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        !detectionBusyRef.current
      ) {
        detectionBusyRef.current = true;
        try {
          const detections = await detector.detect(video);
          const payload = detections.find((item) => item.rawValue?.trim())?.rawValue;
          if (payload && detectionEnabledRef.current) {
            pauseDetection();
            detectedPayloadHandlerRef.current(payload);
            return;
          }
        } catch {
          // Individual frames can fail while a mobile camera is settling.
        } finally {
          detectionBusyRef.current = false;
        }
      }

      if (detectionEnabledRef.current) {
        detectionFrameRef.current = requestAnimationFrame(() => {
          void detectFrame();
        });
      }
    };

    void detectFrame();
  }, [pauseDetection]);

  const releaseCamera = useCallback(() => {
    pauseDetection();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    detectorRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, [pauseDetection]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      releaseCamera();
    };
  }, [releaseCamera]);

  useEffect(() => {
    if (settings.cameraEnabled) return;
    releaseCamera();
    setCameraState("disabled");
    setCameraMessage(
      "Pentadbir telah mematikan kamera. Simulasi dan kod manual masih boleh digunakan.",
    );
  }, [releaseCamera, settings.cameraEnabled]);

  const automaticStatus = useCallback((): AttendanceStatus => {
    const now = new Date();
    const localTime = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes(),
    ).padStart(2, "0")}`;
    const isLate =
      localTime > settings.lateAfter &&
      settings.attendanceStatuses.includes("late");
    return isLate ? "late" : "present";
  }, [settings.attendanceStatuses, settings.lateAfter]);

  const processPayload = useCallback(
    (payload: string, method: AttendanceMethod = "qr") => {
      const normalized = payload.trim();
      setLastPayload(normalized);
      pauseDetection();
      if (streamRef.current) setCameraState("paused");

      const code = normalized.includes("|")
        ? normalized.split("|")[1]?.trim()
        : normalized;
      const matchedStudent = students.find(
        (student) => student.code.toLowerCase() === code?.toLowerCase(),
      );

      if (matchedStudent && !scopedStudentIds.has(matchedStudent.id)) {
        setResult({
          ok: false,
          kind: "not_found",
          student: matchedStudent,
          message: `${matchedStudent.name} bukan dalam skop ${scopeName}. Tukar kelas atau imbas murid lain.`,
        });
        return;
      }

      setResult(markAttendance(normalized, automaticStatus(), method));
    },
    [
      automaticStatus,
      markAttendance,
      pauseDetection,
      scopeName,
      scopedStudentIds,
      students,
    ],
  );

  detectedPayloadHandlerRef.current = (payload) => processPayload(payload, "qr");

  const startCamera = useCallback(async () => {
    setResult(null);
    if (!settings.cameraEnabled) {
      setCameraState("disabled");
      setCameraMessage(
        "Kamera dimatikan dalam Tetapan. Gunakan simulasi atau masukkan kod murid.",
      );
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraState("unavailable");
      setCameraMessage(
        "Pelayar ini tidak menyediakan akses kamera. Gunakan simulasi atau kod manual.",
      );
      return;
    }

    releaseCamera();
    const requestId = ++requestIdRef.current;
    setCameraState("requesting");
    setCameraMessage("Benarkan akses kamera apabila diminta oleh pelayar.");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
      });

      if (!mountedRef.current || requestId !== requestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        releaseCamera();
        setCameraState("unavailable");
        setCameraMessage("Pratonton kamera tidak dapat dibuka. Cuba sekali lagi.");
        return;
      }
      video.srcObject = stream;
      await video.play();

      const Detector = (
        window as Window & {
          BarcodeDetector?: BarcodeDetectorConstructor;
        }
      ).BarcodeDetector;

      if (Detector) {
        try {
          detectorRef.current = new Detector({ formats: ["qr_code"] });
        } catch {
          detectorRef.current = null;
        }
      }

      setCameraState("active");
      if (detectorRef.current) {
        setCameraMessage("Halakan kod QR murid di dalam bingkai.");
        beginDetection();
      } else {
        setCameraMessage(
          "Pratonton aktif, tetapi pengesanan QR automatik tidak disokong. Gunakan simulasi atau kod manual.",
        );
      }
    } catch (error) {
      releaseCamera();
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      const errorName = error instanceof DOMException ? error.name : "";
      if (errorName === "NotAllowedError" || errorName === "SecurityError") {
        setCameraState("denied");
        setCameraMessage(
          "Akses kamera tidak dibenarkan. Benarkan kamera dalam pelayar, atau terus guna pilihan di bawah.",
        );
      } else {
        setCameraState("unavailable");
        setCameraMessage(
          "Kamera tidak dapat dimulakan. Semak kamera peranti atau gunakan pilihan di bawah.",
        );
      }
    }
  }, [beginDetection, releaseCamera, settings.cameraEnabled]);

  const stopCamera = useCallback(() => {
    requestIdRef.current += 1;
    releaseCamera();
    setCameraState(settings.cameraEnabled ? "idle" : "disabled");
    setCameraMessage(
      settings.cameraEnabled
        ? "Kamera dihentikan. Anda boleh mulakan semula bila-bila masa."
        : "Kamera dimatikan dalam Tetapan.",
    );
  }, [releaseCamera, settings.cameraEnabled]);

  const handleManualSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!manualCode.trim()) {
      setLastPayload("");
      pauseDetection();
      if (streamRef.current) setCameraState("paused");
      setResult({
        ok: false,
        kind: "not_found",
        message: "Masukkan kod murid sebelum meneruskan.",
      });
      return;
    }
    processPayload(manualCode, "manual");
  };

  const handleSimulation = () => {
    if (!nextUnmarkedStudent) return;
    processPayload(nextUnmarkedStudent.qrPayload, "qr");
  };

  const handleNextScan = () => {
    setResult(null);
    setManualCode("");
    setLastPayload("");
    if (streamRef.current) {
      setCameraState("active");
      if (detectorRef.current) {
        setCameraMessage("Halakan kod QR murid seterusnya di dalam bingkai.");
        beginDetection();
      }
    }
  };

  const recentRecords = useMemo(
    () =>
      todayRecords
        .filter((record) => scopedStudentIds.has(record.studentId))
        .sort(
          (left, right) =>
            new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime(),
        )
        .slice(0, 6)
        .map((record) => ({
          record,
          student: students.find((student) => student.id === record.studentId),
        }))
        .filter((item) => item.student),
    [scopedStudentIds, students, todayRecords],
  );

  const cameraIsRunning =
    cameraState === "active" || cameraState === "paused";
  const resultTone =
    result?.kind === "success"
      ? "success"
      : result?.kind === "duplicate"
        ? "warning"
        : "danger";

  return (
    <main className="scanner-page">
      <PageHeader
        eyebrow="Operasi harian"
        title="Imbas Kehadiran"
        description={`${formatDayDate(today)} · ${scopeName}`}
      />

      <div className="scanner-workspace">
        <section className="scanner-primary" aria-labelledby="scanner-camera-title">
          <div className="scanner-scopebar">
            <div>
              <span className="scanner-scopebar__label">Skop imbasan</span>
              {canChooseScope ? (
                <label className="scanner-class-select">
                  <span className="sr-only">Pilih skop kelas</span>
                  <Users size={17} aria-hidden="true" />
                  <select
                    value={selectedClassId}
                    onChange={(event) => setSelectedClassId(event.target.value)}
                    disabled={cameraState === "requesting"}
                  >
                    <option value="all">Semua kelas</option>
                    {availableClasses.map((schoolClass) => (
                      <option key={schoolClass.id} value={schoolClass.id}>
                        {schoolClass.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <strong>{scopeName}</strong>
              )}
            </div>
            <div className="scanner-progress" aria-label={`${recordedCount} daripada ${scopedStudents.length} murid direkod`}>
              <strong>{recordedCount}</strong>
              <span>/ {scopedStudents.length} direkod</span>
            </div>
          </div>

          <div
            className={`scanner-viewfinder scanner-viewfinder--${cameraState}`}
            aria-label="Pratonton kamera pengimbas QR"
          >
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              aria-hidden={!cameraIsRunning}
            />
            {!cameraIsRunning && (
              <div className="scanner-viewfinder__empty">
                {cameraState === "denied" ||
                cameraState === "unavailable" ||
                cameraState === "disabled" ? (
                  <CameraOff size={34} aria-hidden="true" />
                ) : (
                  <ScanLine size={38} aria-hidden="true" />
                )}
                <strong id="scanner-camera-title">{cameraStateLabel[cameraState]}</strong>
                <span>Kod QR kekal unik bagi setiap murid.</span>
              </div>
            )}
            {cameraIsRunning && (
              <div className="scanner-reticle" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </div>
            )}
            <span className={`scanner-camera-state scanner-camera-state--${cameraState}`}>
              <span aria-hidden="true" />
              {cameraStateLabel[cameraState]}
            </span>
          </div>

          <p
            className={`scanner-camera-message scanner-camera-message--${cameraState}`}
            role={cameraState === "denied" ? "alert" : "status"}
          >
            {cameraMessage}
          </p>

          <div className="scanner-camera-action">
            {cameraIsRunning ? (
              <Button
                variant="secondary"
                icon={Square}
                onClick={stopCamera}
                full
              >
                Hentikan kamera
              </Button>
            ) : (
              <Button
                icon={Camera}
                onClick={() => void startCamera()}
                loading={cameraState === "requesting"}
                disabled={!settings.cameraEnabled}
                full
              >
                {cameraState === "denied" || cameraState === "unavailable"
                  ? "Cuba kamera lagi"
                  : "Mulakan kamera"}
              </Button>
            )}
          </div>

          <div className="scanner-fallbacks" aria-label="Pilihan imbasan alternatif">
            <div className="scanner-simulation">
              <div>
                <FlaskConical size={19} aria-hidden="true" />
                <span>
                  <strong>Simulasi imbas</strong>
                  <small>
                    {nextUnmarkedStudent
                      ? `Murid seterusnya: ${nextUnmarkedStudent.name}`
                      : `Semua murid aktif dalam ${scopeName} sudah direkod`}
                  </small>
                </span>
              </div>
              <Button
                variant="secondary"
                icon={UserCheck}
                onClick={handleSimulation}
                disabled={!nextUnmarkedStudent}
              >
                Simulasi
              </Button>
            </div>

            <form className="scanner-manual" onSubmit={handleManualSubmit}>
              <label htmlFor="scanner-manual-code">
                <Keyboard size={18} aria-hidden="true" />
                Masukkan kod murid
              </label>
              <div>
                <input
                  id="scanner-manual-code"
                  value={manualCode}
                  onChange={(event) => setManualCode(event.target.value)}
                  placeholder="Contoh: STJ-2026-0001"
                  autoCapitalize="characters"
                  autoComplete="off"
                />
                <Button type="submit" variant="secondary">
                  Semak kod
                </Button>
              </div>
            </form>
          </div>
        </section>

        <aside className="scanner-recent panel" aria-labelledby="recent-scans-title">
          <header className="panel__header">
            <div>
              <h2 id="recent-scans-title">Rekod hari ini</h2>
              <p>{scopeName}</p>
            </div>
            <span className="scanner-recent__count">{recordedCount}</span>
          </header>
          <div className="scanner-recent__list">
            {recentRecords.length ? (
              recentRecords.map(({ record, student }, index) =>
                student ? (
                  <article className="scanner-recent-row" key={record.id}>
                    <Avatar
                      initials={student.initials}
                      size="sm"
                      tone={index}
                      label={student.name}
                    />
                    <div>
                      <strong>{student.name}</strong>
                      <span>
                        {student.className} · {formatTime(record.timestamp)}
                      </span>
                    </div>
                    <StatusBadge status={record.status} compact />
                  </article>
                ) : null,
              )
            ) : (
              <div className="scanner-recent__empty">
                <Clock3 size={22} aria-hidden="true" />
                <strong>Belum ada rekod</strong>
                <span>Imbasan berjaya akan muncul di sini.</span>
              </div>
            )}
          </div>
        </aside>
      </div>

      <Modal
        open={Boolean(result)}
        onClose={handleNextScan}
        title={result ? resultHeading(result) : "Keputusan imbasan"}
        description={result?.message}
        size="sm"
        footer={
          <Button
            icon={result?.kind === "success" ? ScanLine : undefined}
            onClick={handleNextScan}
            full
          >
            {result?.kind === "success"
              ? "Imbas murid seterusnya"
              : "Cuba kod lain"}
          </Button>
        }
      >
        {result && (
          <div className={`scan-result scan-result--${resultTone}`}>
            <div className="scan-result__symbol" aria-hidden="true">
              {result.kind === "success" ? (
                <CheckCircle2 size={30} />
              ) : result.kind === "duplicate" ? (
                <Clock3 size={30} />
              ) : (
                <AlertTriangle size={30} />
              )}
            </div>

            {result.student ? (
              <>
                <div className="scan-result__student">
                  <Avatar
                    initials={result.student.initials}
                    size="lg"
                    tone={2}
                    label={result.student.name}
                  />
                  <div>
                    <strong>{result.student.name}</strong>
                    <span>
                      {result.student.className} · {result.student.code}
                    </span>
                  </div>
                </div>
                <dl className="scan-result__details">
                  <div>
                    <dt>Status</dt>
                    <dd>
                      {result.record ? (
                        <StatusBadge status={result.record.status} />
                      ) : (
                        <span>Tidak direkod</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Tarikh</dt>
                    <dd>{formatDayDate(today)}</dd>
                  </div>
                  {result.record && (
                    <>
                      <div>
                        <dt>Masa</dt>
                        <dd className="tabular">{formatTime(result.record.timestamp)}</dd>
                      </div>
                      <div>
                        <dt>Direkod oleh</dt>
                        <dd>{result.record.recordedBy}</dd>
                      </div>
                    </>
                  )}
                </dl>
              </>
            ) : (
              <div className="scan-result__unknown">
                <ScanLine size={25} aria-hidden="true" />
                <div>
                  <strong>Tiada profil sepadan</strong>
                  <span>
                    {lastPayload
                      ? `Kod diperiksa: ${lastPayload}`
                      : "Belum ada kod dimasukkan."}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <div className="sr-only" aria-live="polite">
        {result?.message}
      </div>
    </main>
  );
}


